<template>
  <Teleport to="body">
    <div v-if="open" class="mv-sheet-overlay" @click="close">
      <div class="mv-sheet mv-macro-sheet" @click.stop>
        <div class="mv-sheet-handle"></div>

        <!-- Header -->
        <div class="mv-macro-head">
          <div class="mv-macro-search-wrap">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input v-model="macroSearch" class="mv-macro-search" placeholder="Buscar macro…" />
          </div>
          <span class="mv-macro-count">{{ filteredMacros.length }}</span>
        </div>

        <!-- Carregando -->
        <div v-if="macrosLoading" class="mv-macro-loading">
          <div class="mv-spinner" style="width:22px;height:22px;border-width:2px;"></div>
        </div>

        <!-- Formulário (send_webhook_event_form) -->
        <template v-else-if="macroFormStep">
          <div class="mv-macro-form-title">
            <button class="mv-macro-back-btn" @click="macroFormStep = false">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Voltar
            </button>
            <span>{{ macroRunningObj?.name }}</span>
          </div>
          <div class="mv-macro-form-fields">
            <div v-for="f in macroFormFields" :key="f.idx + '_' + f.key" class="mv-macro-form-group">
              <label class="mv-macro-form-label">{{ f.label || f.key }}</label>
              <input v-model="f.value" class="mv-macro-form-input" :placeholder="f.label || f.key" />
            </div>
          </div>
          <div class="mv-macro-form-actions">
            <button class="mv-macro-run-btn" @click="submitMacroForm" :disabled="macroRunning">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              Executar
            </button>
          </div>
        </template>

        <!-- Resultado de execução -->
        <template v-else-if="macroRunning || macroRunDone">
          <div class="mv-macro-result-title">
            <button class="mv-macro-back-btn" @click="resetMacroRun" :disabled="macroRunning">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Voltar
            </button>
            <span>{{ macroRunningObj?.name }}</span>
          </div>
          <div class="mv-macro-steps">
            <div v-for="(r, i) in macroStepResults" :key="i" class="mv-macro-step" :class="r.ok ? 'mv-macro-step--ok' : 'mv-macro-step--err'">
              <span class="mv-macro-step-icon">
                <svg v-if="r.ok" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </span>
              <div class="mv-macro-step-body">
                <div class="mv-macro-step-label">{{ r.label }}</div>
                <div v-if="!r.ok" class="mv-macro-step-err">{{ r.msg }}</div>
              </div>
            </div>
            <!-- Passo atual sendo executado -->
            <div v-if="macroRunning" class="mv-macro-step mv-macro-step--pending">
              <span class="mv-macro-step-spin"></span>
              <div class="mv-macro-step-body">
                <div class="mv-macro-step-label">
                  {{ macroRunningObj?.actions[macroStepResults.length]
                     ? (MACRO_ACTION_LABELS[macroRunningObj.actions[macroStepResults.length].action_name] ?? '…')
                     : '…' }}
                </div>
              </div>
            </div>
          </div>
          <!-- Resumo final -->
          <div v-if="macroRunDone" class="mv-macro-summary" :class="macroStepResults.every(r => r.ok) ? 'mv-macro-summary--ok' : 'mv-macro-summary--warn'">
            <svg v-if="macroStepResults.every(r => r.ok)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span v-if="macroStepResults.every(r => r.ok)">Concluído com sucesso!</span>
            <span v-else>{{ macroStepResults.filter(r => !r.ok).length }} ação(ões) com erro</span>
          </div>
        </template>

        <!-- Lista de macros -->
        <template v-else>
          <div v-if="!macrosList.length && !macrosLoading" class="mv-macro-empty">
            Nenhuma macro encontrada.
          </div>
          <div class="mv-macro-list">
            <button v-for="m in filteredMacros" :key="m.id" class="mv-macro-item" @click="runMacro(m)">
              <div class="mv-macro-item-info">
                <span class="mv-macro-item-name">{{ m.name }}</span>
                <span class="mv-macro-item-count">{{ m.actions.length }} ação{{ m.actions.length !== 1 ? 'ões' : '' }}</span>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;color:#BC0C24">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
            </button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Conversation } from '@/types/dashboard'
import { listMacros, type Macro } from '@/services/macrosService'
import { runMacroAction, MACRO_ACTION_LABELS, type MacroActionContext } from '@/services/macroEngineService'
import { fetchConversation } from '@/services/conversationActionsService'

const props = defineProps<{
  open: boolean
  convId: number | null
  conv: Conversation | null
  token: string
  accountId: number | string | null | undefined
}>()

const emit = defineEmits<{
  close: []
  'run-done': [convId: number]
}>()

function close() { emit('close') }

/* ── Estado ─────────────────── */
const macrosList       = ref<Macro[]>([])
const macrosLoading    = ref(false)
const macroSearch      = ref('')
const macroRunning     = ref(false)
const macroRunDone     = ref(false)
const macroRunningObj  = ref<Macro | null>(null)
const macroStepResults = ref<{ label: string; ok: boolean; msg: string }[]>([])
const macroFormStep    = ref(false)
const macroFormFields  = ref<{ idx: number; key: string; label: string; value: string }[]>([])

const filteredMacros = computed(() => {
  const q          = macroSearch.value.trim().toLowerCase()
  const convTeamId = props.conv?.team?.id ?? null
  const active     = macrosList.value.filter(m => {
    if (!m.is_active) return false
    if (!m.team_ids?.length) return true
    return convTeamId ? m.team_ids.includes(convTeamId) : true
  })
  return q ? active.filter(m => m.name.toLowerCase().includes(q)) : active
})

async function loadMacros(force = false) {
  if (macrosList.value.length && !force) return
  macrosLoading.value = true
  try {
    macrosList.value = await listMacros(props.token)
  } catch { /* silencioso */ } finally { macrosLoading.value = false }
}

function resetMacroRun() {
  macroRunning.value     = false
  macroRunDone.value     = false
  macroRunningObj.value  = null
  macroStepResults.value = []
  macroFormStep.value    = false
  macroFormFields.value  = []
}

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    loadMacros()
    resetMacroRun()
  } else {
    macroSearch.value = ''
    resetMacroRun()
  }
})

async function buildMacroContext(macro: Macro): Promise<MacroActionContext> {
  const conv = props.conv
  const base: MacroActionContext = {
    contactPhone:       conv?.contact?.phone ?? null,
    contactName:        conv?.contact?.name  ?? null,
    contactEmail:       (conv?.contact as any)?.email ?? null,
    teamId:             conv?.team?.id   ?? null,
    teamName:           conv?.team?.name ?? null,
    conversationStatus: conv?.status ?? null,
    contactId:          conv?.contact?.id ?? null,
    assigneeId:         conv?.assignee?.id ?? null,
    assigneeName:       conv?.assignee?.name ?? null,
    labels:             conv?.labels ?? [],
    accountId:          props.accountId ?? null,
    macroId:            macro.id,
    macroName:          macro.name,
  }
  const needsLiveContext = macro.actions.some(a => a.action_name === 'send_webhook_event' || a.action_name === 'send_webhook_event_form')
  if (!needsLiveContext || !conv) return base
  const data = await fetchConversation(conv.id, props.token)
  if (!data) return base
  return {
    ...base,
    contactEmail: data?.meta?.sender?.email ?? base.contactEmail,
    inboxId:      data?.inbox_id ?? null,
    inboxName:    data?.channel ?? null,
    rawConversation: data,
  }
}

async function executeMacroActions(macro: Macro, formValuesByIdx: Record<number, Record<string, string>> = {}) {
  if (!props.convId) return
  macroRunningObj.value  = macro
  macroStepResults.value = []
  macroRunDone.value     = false
  macroRunning.value     = true
  macroFormStep.value    = false
  const convId = props.convId
  const context = await buildMacroContext(macro)
  for (const [idx, action] of macro.actions.entries()) {
    const formVals = action.action_name === 'send_webhook_event_form'
      ? (formValuesByIdx[idx] ?? {})
      : undefined
    macroStepResults.value.push(await runMacroAction(action, convId, props.token, context, formVals))
  }
  macroRunning.value = false
  macroRunDone.value = true
  emit('run-done', convId)
}

function runMacro(macro: Macro) {
  const formActions = macro.actions
    .map((a, idx) => ({ action: a, idx }))
    .filter(({ action }) => action.action_name === 'send_webhook_event_form')

  if (formActions.length) {
    macroRunningObj.value = macro
    macroFormFields.value = []
    for (const { action, idx } of formActions) {
      const cfg    = action.action_params?.[0] ?? {}
      const fields: Array<{ key: string; label: string }> = Array.isArray(cfg.fields) ? cfg.fields : []
      for (const field of fields) {
        macroFormFields.value.push({ idx, key: field.key, label: field.label || field.key, value: '' })
      }
    }
    macroFormStep.value = true
  } else {
    executeMacroActions(macro)
  }
}

function submitMacroForm() {
  if (!macroRunningObj.value) return
  const formValuesByIdx: Record<number, Record<string, string>> = {}
  for (const f of macroFormFields.value) {
    if (!formValuesByIdx[f.idx]) formValuesByIdx[f.idx] = {}
    formValuesByIdx[f.idx][f.key] = f.value
  }
  executeMacroActions(macroRunningObj.value, formValuesByIdx)
}
</script>

<style scoped>
/* Compartilhado com outros bottom sheets */
.mv-sheet-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.4);
  z-index: 2000;
  display: flex; flex-direction: column; justify-content: flex-end;
}
.mv-sheet {
  background: #fff;
  border-radius: 18px 18px 0 0;
  padding: 12px 0 max(20px, env(safe-area-inset-bottom));
  max-height: 72vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.mv-sheet-handle {
  width: 36px; height: 4px;
  background: #E5E7EB; border-radius: 2px;
  margin: 0 auto 14px;
}
.mv-spinner {
  width: 32px; height: 32px;
  border: 3px solid #E5E7EB;
  border-top-color: #BC0C24;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ══ Macro sheet ══ */
.mv-macro-sheet { max-height: 80vh; display: flex; flex-direction: column; }

.mv-macro-head {
  display: flex; align-items: center; gap: 8px;
  padding: 0 14px 10px;
}
.mv-macro-search-wrap {
  flex: 1; display: flex; align-items: center; gap: 7px;
  background: #F3F4F6; border-radius: 9px; padding: 8px 12px;
}
.mv-macro-search {
  flex: 1; border: none; background: transparent; outline: none;
  font-size: 13px; font-family: 'Poppins', sans-serif; color: #1A1A1A;
}
.mv-macro-search::placeholder { color: #9CA3AF; }
.mv-macro-count {
  font-size: 11px; font-weight: 700; color: #BC0C24;
  background: rgba(188,12,36,.1); padding: 2px 8px;
  border-radius: 20px; flex-shrink: 0;
}

.mv-macro-loading { display: flex; justify-content: center; padding: 28px; }

.mv-macro-list { overflow-y: auto; flex: 1; }

.mv-macro-item {
  display: flex; align-items: center; gap: 12px;
  width: 100%; padding: 14px 18px;
  background: none; border: none;
  border-bottom: 1px solid #F3F4F6;
  cursor: pointer; text-align: left;
  font-family: 'Poppins', sans-serif;
  -webkit-tap-highlight-color: transparent;
}
.mv-macro-item:active { background: #F7F7F7; }
.mv-macro-item:last-child { border-bottom: none; }
.mv-macro-item-info { flex: 1; min-width: 0; }
.mv-macro-item-name {
  display: block; font-size: 14px; font-weight: 600; color: #1A1A1A;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mv-macro-item-count { font-size: 11px; color: #9CA3AF; margin-top: 1px; display: block; }

.mv-macro-empty {
  padding: 32px 18px; text-align: center;
  font-size: 13px; color: #9CA3AF;
}

/* ── Resultado de execução ── */
.mv-macro-result-title,
.mv-macro-form-title {
  display: flex; align-items: center; gap: 10px;
  padding: 0 18px 10px;
  font-size: 13px; font-weight: 700; color: #374151;
  border-bottom: 1px solid #F3F4F6;
  margin-bottom: 4px;
}
.mv-macro-back-btn {
  display: flex; align-items: center; gap: 4px;
  background: none; border: none;
  font-size: 12px; font-weight: 600; color: #6B7280;
  font-family: 'Poppins', sans-serif;
  cursor: pointer; padding: 4px 8px 4px 4px;
  border-radius: 6px; flex-shrink: 0;
}
.mv-macro-back-btn:active { background: #F3F4F6; }
.mv-macro-back-btn:disabled { opacity: .5; }

.mv-macro-steps { overflow-y: auto; flex: 1; padding: 6px 0; }

.mv-macro-step {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 18px;
  border-bottom: 1px solid #F9FAFB;
}
.mv-macro-step--ok   .mv-macro-step-icon { color: #22c55e; }
.mv-macro-step--err  .mv-macro-step-icon { color: #ef4444; }
.mv-macro-step--pending .mv-macro-step-icon { width: 18px; flex-shrink: 0; }
.mv-macro-step-icon {
  width: 18px; height: 18px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  margin-top: 1px;
}
.mv-macro-step-spin {
  display: inline-block;
  width: 13px; height: 13px;
  border: 2px solid #E5E7EB;
  border-top-color: #BC0C24;
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
.mv-macro-step-body { flex: 1; }
.mv-macro-step-label { font-size: 13px; font-weight: 500; color: #1A1A1A; }
.mv-macro-step-err   { font-size: 11px; color: #ef4444; margin-top: 2px; }

.mv-macro-summary {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid #F3F4F6;
  font-size: 13px; font-weight: 700;
}
.mv-macro-summary--ok   { color: #15803d; }
.mv-macro-summary--warn { color: #b45309; }

/* ── Formulário de macro (send_webhook_event_form) ── */
.mv-macro-form-fields { padding: 10px 18px; overflow-y: auto; flex: 1; }
.mv-macro-form-group  { margin-bottom: 14px; }
.mv-macro-form-label  { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 5px; }
.mv-macro-form-input  {
  width: 100%; box-sizing: border-box;
  border: 1.5px solid #E5E7EB; border-radius: 9px;
  padding: 9px 12px;
  font-size: 14px; font-family: 'Poppins', sans-serif; color: #1A1A1A;
  outline: none; background: #F9FAFB;
}
.mv-macro-form-input:focus { border-color: #BC0C24; background: #fff; }
.mv-macro-form-actions {
  padding: 10px 18px max(16px, env(safe-area-inset-bottom));
  border-top: 1px solid #F3F4F6;
}
.mv-macro-run-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 13px;
  background: #BC0C24; color: #fff; border: none;
  border-radius: 10px; font-size: 14px; font-weight: 700;
  font-family: 'Poppins', sans-serif; cursor: pointer;
}
.mv-macro-run-btn:disabled { opacity: .5; }
.mv-macro-run-btn:not(:disabled):active { background: #73010F; }
</style>
